import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';
import { createTailwindMerge, getDefaultConfig } from 'tailwind-merge';

/**
 * Custom tailwind-merge that recognises our design.md font-size tokens.
 *
 * Problem: tailwind-merge's font-size group matches `text-{size}`. Our custom
 * tokens (text-eyebrow, text-h1, etc.) are not in the default config, so
 * tailwind-merge treats them as text-color and strips them when a real
 * text-color class (text-muted) follows.
 *
 * Fix: extend the font-size class group's `text` array to include our tokens.
 */
const customTwMerge = createTailwindMerge(() => {
  const config = getDefaultConfig();
  // Find the font-size class group and extend its `text` matcher
  const fontSizeGroup = config.classGroups['font-size'];
  if (fontSizeGroup && Array.isArray(fontSizeGroup)) {
    const textEntry = fontSizeGroup.find(
      (e: unknown) => typeof e === 'object' && e !== null && 'text' in (e as Record<string, unknown>),
    ) as { text: unknown[] } | undefined;
    if (textEntry) {
      textEntry.text = [
        ...textEntry.text,
        'display', 'h1', 'h2', 'h3', 'body', 'body-lg', 'small', 'eyebrow', 'mono',
        'headline', 'title', 'label',
      ];
    }
  }
  return config;
});

/**
 * Merge Tailwind classes with clsx + tailwind-merge.
 * Standard shadcn/ui utility — resolves conflicting Tailwind utilities.
 */
export function cn(...inputs: ClassValue[]): string {
  return customTwMerge(clsx(inputs));
}
